import { forwardRef } from 'react';
import { Tourbook } from '@root/types';
import { useTranslation } from 'react-i18next';
import { Image, Transformation } from 'cloudinary-react';
import useEnv from '@shared/useEnv';
import cn from 'classnames';
import useAltText from '@root/shared/useAltText';
import Button from '@components/shared/V2Button';
import EditTourbookModal from '@components/shared/EditTourbookModal';
import { isInIframe } from '@root/shared/iframeUtils';
import ClientInformation from './ClientInformation';
import ContactInformation from './ContactInformation';
import tourbookStyles from './Header.module.less';

type Props = {
  tourbook: Tourbook;
  readOnly: boolean;
  onEditClick: () => void;
  onCloseModal: () => void;
  isModalOpen: boolean;
  hidden: boolean;
};
const Header = forwardRef<HTMLDivElement | null, Props>(
  ({ tourbook, readOnly, onEditClick, onCloseModal, isModalOpen, hidden }: Props, ref) => {
    const { getAltTextForTourbookHeader } = useAltText();
    const { t } = useTranslation('tourbook');
    const { cloudinaryCdn, cloudinaryCloud } = useEnv();

    const containsDescriptionBodyElements = tbook =>
      tbook.name && (tbook.description || tbook.clientName || tbook.clientLogoImage);

    return (
      <div
        id="tourbook-header"
        className={cn(tourbookStyles.headerContainer, isInIframe() && '!mt-3')}
        ref={ref}
      >
        <header className={cn([tourbookStyles.header, hidden && tourbookStyles.hidden])}>
          <div className={tourbookStyles.headerImageContainer}>
            {tourbook.headerImage ? (
              <Image
                className={tourbookStyles.headerImage}
                cloudName={cloudinaryCloud}
                privateCdn={cloudinaryCdn}
                publicId={tourbook.headerImage.cloudinaryId}
                alt={getAltTextForTourbookHeader(tourbook.name)}
              >
                <Transformation
                  crop="fill"
                  fetchFormat="jpg"
                  quality="100"
                  width="1500"
                  height="1500"
                  gravity="auto"
                />
              </Image>
            ) : (
              <>
                <div className={tourbookStyles.uploadHeaderImageContainer}>
                  {!readOnly && (
                    <Button
                      type="quarternary"
                      onClick={onEditClick}
                      className={tourbookStyles.uploadHeaderImage}
                    >
                      {t('header.uploadHeaderImage')}
                    </Button>
                  )}
                </div>
                <img
                  className={tourbookStyles.headerImagePlaceholder}
                  src="/images/tourbook-header-default.jpg"
                  alt="Tourbook Header"
                />
              </>
            )}
          </div>
          <div className={tourbookStyles.descriptionSection}>
            <div className={tourbookStyles.descriptionBody}>
              <div
                className={cn([
                  tourbookStyles.descriptionBody,
                  containsDescriptionBodyElements(tourbook) &&
                    tourbookStyles.withDescriptionContent,
                ])}
              >
                <div className="flex flex-col gap-1">
                  <h4 className={tourbookStyles.descriptionTitle}>{tourbook.name}</h4>
                  {tourbook.description && (
                    <span className={tourbookStyles.description}>{tourbook.description}</span>
                  )}
                </div>
                <ClientInformation name={tourbook.clientName} image={tourbook.clientLogoImage} />
              </div>
            </div>
          </div>

          <section className={tourbookStyles.contactSection}>
            {tourbook && (
              <ContactInformation user={tourbook.owner} readOnly={readOnly || isInIframe()} />
            )}
          </section>
          <EditTourbookModal
            tourbook={tourbook}
            isModalOpen={isModalOpen}
            onCloseModal={onCloseModal}
          />
        </header>
      </div>
    );
  },
);

export default Header;
